/**
 * The application shell — providers, routes and the route guards (T2.11).
 *
 * Provider order matters: QueryClientProvider is outermost because AuthProvider
 * is built on a query; TermsProvider sits inside auth and serves the system
 * defaults, and BlueprintRoute nests a second one carrying that blueprint's
 * resolved terminology once it has loaded.
 *
 * Every route except /login is wrapped in RequireAuth, and the admin routes
 * additionally in RequireAdmin. The guard is on the ROUTE rather than inside the
 * screen, so a pasted URL cannot reach a screen its role may not have — see the
 * note at the top of guards.tsx about why this is a courtesy and the API is the
 * enforcement.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router';
import { createQueryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './lib/auth';
import { TermsProvider, useTerms } from './lib/terms';
import { ThemeProvider, useTheme } from './lib/theme';
import { ViewTabsSlot, ViewTabsSlotProvider } from './lib/viewTabs';
import { RequireAdmin, RequireAuth, RedirectIfSignedIn } from './routes/guards';
import { LoginRoute } from './routes/LoginRoute';
import { BlueprintsRoute } from './routes/BlueprintsRoute';
import { BlueprintRoute } from './routes/BlueprintRoute';
import { AccountRoute } from './routes/AccountRoute';
import {
  AdminAccessRoute,
  AdminBlueprintRoute,
  AdminClientRoute,
  AdminClientsRoute,
  AdminArchiveRoute,
  AdminUsersRoute,
} from './routes/AdminRoutes';
import { NotFoundRoute } from './routes/placeholders';

const queryClient = createQueryClient();

/**
 * The tab bar and theme toggle, using the generator's own class names so the
 * ported CSS applies without a new rule.
 *
 * Admin links are ABSENT for a non-admin, not disabled — a disabled control
 * still advertises a feature the user may not have.
 */
function Chrome() {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, signOut } = useAuth();
  const terms = useTerms();

  return (
    <nav className="viewtabs">
      {/* While a password change is required nothing else is reachable, so nothing else is offered. */}
      {isAuthenticated && !user?.mustChangePassword ? (
        <Link className="themetoggle" to="/blueprints">
          {terms.plural('blueprint')}
        </Link>
      ) : null}
      {user?.globalRole === 'admin' && !user.mustChangePassword ? (
        <>
          <Link className="themetoggle" to="/admin/users">
            Users
          </Link>
          <Link className="themetoggle" to="/admin/clients">
            Clients
          </Link>
          <Link className="themetoggle" to="/admin/access">
            Access
          </Link>
          <Link className="themetoggle" to="/admin/archive">
            Archive
          </Link>
        </>
      ) : null}
      <ViewTabsSlot />
      <span className="spacer" />
      {user ? (
        <Link className="chrome-user" to="/account">
          {user.name}
        </Link>
      ) : null}
      <button
        type="button"
        className="themetoggle"
        onClick={toggleTheme}
        aria-label={`Switch to the ${theme === 'dark' ? 'light' : 'dark'} theme`}
      >
        <span className="glyph" aria-hidden="true">
          {theme === 'dark' ? '☾' : '☀'}
        </span>
        {theme === 'dark' ? 'Dark' : 'Light'}
      </button>
      {isAuthenticated ? (
        <button type="button" className="themetoggle" onClick={() => void signOut()}>
          Sign out
        </button>
      ) : null}
    </nav>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TermsProvider>
          <ThemeProvider>
            <ViewTabsSlotProvider>
            <BrowserRouter>
              <div className="wrap">
                <Chrome />
                <Routes>
                  <Route path="/" element={<Navigate to="/blueprints" replace />} />
                  <Route
                    path="/login"
                    element={
                      <RedirectIfSignedIn>
                        <LoginRoute />
                      </RedirectIfSignedIn>
                    }
                  />
                  <Route
                    path="/blueprints"
                    element={
                      <RequireAuth>
                        <BlueprintsRoute />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/blueprints/:id"
                    element={
                      <RequireAuth>
                        <BlueprintRoute />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <RequireAuth allowPasswordChange>
                        <AccountRoute />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <RequireAdmin>
                        <AdminUsersRoute />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/clients"
                    element={
                      <RequireAdmin>
                        <AdminClientsRoute />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/clients/:clientId"
                    element={
                      <RequireAdmin>
                        <AdminClientRoute />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/blueprints/:id"
                    element={
                      <RequireAdmin>
                        <AdminBlueprintRoute />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/access"
                    element={
                      <RequireAdmin>
                        <AdminAccessRoute />
                      </RequireAdmin>
                    }
                  />
                  <Route
                    path="/admin/archive"
                    element={
                      <RequireAdmin>
                        <AdminArchiveRoute />
                      </RequireAdmin>
                    }
                  />
                  {/* The screen was called "Trash" until Phase 7. The old path
                      stays as a redirect so anything already bookmarked, and
                      the e2e specs written against it, keep working. */}
                  <Route path="/admin/trash" element={<Navigate to="/admin/archive" replace />} />
                  <Route path="*" element={<NotFoundRoute />} />
                </Routes>
              </div>
            </BrowserRouter>
            </ViewTabsSlotProvider>
          </ThemeProvider>
        </TermsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
